/**
 * The per-document editing controls (T7.25): re-head, reorder, remove.
 *
 * This whole module is rendered only where `canEdit` is true. A viewer does not
 * get a disabled version of it — the component is never mounted, so there is no
 * control in the DOM to find, enable or script.
 *
 * Removal names what is being removed. A document has no soft delete and no
 * archive: once the API has it, the bytes are gone, so the confirmation says
 * that rather than asking a bare "are you sure?".
 */
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api';
import {
  useDeleteDocument,
  useMoveDocument,
  useUpdateDocument,
  type BlueprintDocument,
} from '../../lib/documents';
import { Dialog } from '../versions/Dialog';

const HEADING_MAX = 200;

function message(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function DocumentControls({
  blueprintId,
  document: doc,
  siblings,
}: {
  blueprintId: string;
  document: BlueprintDocument;
  /** Every document of the same kind, in the order shown. Reordering is within a kind. */
  siblings: BlueprintDocument[];
}) {
  const update = useUpdateDocument(blueprintId);
  const remove = useDeleteDocument(blueprintId);
  const move = useMoveDocument(blueprintId);

  const [renaming, setRenaming] = useState(false);
  const [heading, setHeading] = useState(doc.heading);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const index = siblings.findIndex((d) => d.id === doc.id);
  const busy = update.isPending || remove.isPending || move.isPending;

  const openRename = () => {
    setHeading(doc.heading);
    setError(null);
    setRenaming(true);
  };

  const saveHeading = async (e: FormEvent) => {
    e.preventDefault();
    const next = heading.trim();
    if (!next || busy) return;
    setError(null);
    try {
      await update.mutateAsync({ id: doc.id, heading: next });
      setRenaming(false);
    } catch (err) {
      setError(message(err, 'The heading could not be saved.'));
    }
  };

  const confirmRemoval = async () => {
    setError(null);
    try {
      await remove.mutateAsync(doc.id);
      setConfirming(false);
    } catch (err) {
      setError(message(err, 'It could not be removed.'));
    }
  };

  const shift = (delta: -1 | 1) => {
    setError(null);
    move.mutate(
      { ordered: siblings, id: doc.id, delta },
      { onError: (err) => setError(message(err, 'The order could not be changed.')) },
    );
  };

  return (
    <div className="doc-actions">
      <button
        type="button"
        className="btn"
        onClick={() => shift(-1)}
        disabled={busy || index <= 0}
        aria-label={`Move ${doc.heading} up`}
        title="Move up"
      >
        ↑
      </button>
      <button
        type="button"
        className="btn"
        onClick={() => shift(1)}
        disabled={busy || index < 0 || index >= siblings.length - 1}
        aria-label={`Move ${doc.heading} down`}
        title="Move down"
      >
        ↓
      </button>
      <button
        type="button"
        className="btn"
        onClick={openRename}
        disabled={busy}
        aria-label={`Edit the heading of ${doc.heading}`}
      >
        Edit heading
      </button>
      <button
        type="button"
        className="btn danger"
        onClick={() => {
          setError(null);
          setConfirming(true);
        }}
        disabled={busy}
        aria-label={`Remove ${doc.heading}`}
      >
        Remove
      </button>

      {error && !renaming && !confirming ? (
        <span className="bp-inline-err" role="alert">
          {error}
        </span>
      ) : null}

      <Dialog
        open={renaming}
        title="Edit the heading"
        onClose={() => setRenaming(false)}
        dismissible={!update.isPending}
      >
        <form onSubmit={saveHeading} className="bp-dialog-form">
          <div className="field">
            <label className="field-label" htmlFor={`doc-heading-${doc.id}`}>
              Heading
            </label>
            <input
              id={`doc-heading-${doc.id}`}
              value={heading}
              maxLength={HEADING_MAX}
              onChange={(e) => setHeading(e.target.value)}
              disabled={update.isPending}
              required
            />
          </div>
          <p className="doc-dialog-note">{doc.filename}</p>
          {error ? (
            <p className="bp-inline-err" role="alert">
              {error}
            </p>
          ) : null}
          <div className="bp-dialog-actions">
            <button
              type="button"
              className="btn"
              onClick={() => setRenaming(false)}
              disabled={update.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn primary"
              disabled={!heading.trim() || update.isPending}
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={confirming}
        title={doc.kind === 'picture' ? 'Remove this picture?' : 'Remove this file?'}
        onClose={() => setConfirming(false)}
        dismissible={!remove.isPending}
      >
        <div className="bp-dialog-form">
          <p>
            <b>{doc.heading}</b> ({doc.filename}) will be removed from this page and deleted. There
            is no copy to restore it from.
          </p>
          {error ? (
            <p className="bp-inline-err" role="alert">
              {error}
            </p>
          ) : null}
          <div className="bp-dialog-actions">
            <button
              type="button"
              className="btn"
              onClick={() => setConfirming(false)}
              disabled={remove.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn danger"
              onClick={() => void confirmRemoval()}
              disabled={remove.isPending}
            >
              {remove.isPending ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
